import React from 'react';
import { Button, Logo, CrossBrowserVideo } from '../../ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {ROUTES} from "../../../constants/routes";

const HeroSection: React.FC = () => {
  const { t } = useTranslation();
  const [logoLoading, setLogoLoading] = React.useState(true);
  const [logoError, setLogoError] = React.useState(false);
  const [videoLoaded, setVideoLoaded] = React.useState(false);

  const handleLogoLoad = () => {
    setLogoLoading(false);
  };

  const handleLogoError = () => {
    setLogoLoading(false);
    setLogoError(true);
  };

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const handleVideoError = () => {
    // Video failed to load, but we have fallbacks
    console.warn('Video failed to load, using fallback');
  };

  // Multiple video sources for maximum browser compatibility
  const videoSources = [
     {
      src: '/videos/zoe-hero-section.mp4',
      type: 'video/mp4'
    }
  ];

  return (
    <section className="relative bg-black text-white min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background with Cross-Browser Support */}
      <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
        <CrossBrowserVideo
          sources={videoSources}
          autoPlay={true}
          muted={true}
          loop={true}
          playsInline={true}
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ 
            filter: videoLoaded ? 'brightness(0.3) contrast(1.1)' : 'brightness(0.1)',
            opacity: videoLoaded ? 1 : 0.7
          }}
          poster="data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20version='1.1'%20width='1920'%20height='1080'%20viewBox='0%200%201920%201080'%3e%3crect%20width='100%25'%20height='100%25'%20fill='%23000000'/%3e%3c/svg%3e"
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
          fallbackImage="/zoe-logo.svg"
        />

        {/* Video Overlay - Expert gradient design */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/80 z-10"></div>

        {/* Additional sophisticated overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/20 to-black/60 z-20"></div>
        
        {/* Loading indicator for video */}
        {!videoLoaded && (
          <div className="absolute inset-0 flex items-center justify-center z-25">
            <div className="text-white/60 text-sm animate-pulse">
              {t('ui.loading', 'Loading video...')}
            </div>
          </div>
        )}
      </div>

      {/* Hero Content - Enhanced for video background */}
      <div className="relative z-30 text-center max-w-7xl mx-auto px-6 pt-16 sm:pt-20 md:pt-8">
        {/* ZOE Logo/Brand - More prominent for video */}
        <div className="mb-8 sm:mb-12">
          <div className="inline-block mb-8">
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="orbiting-particles"></div>

              {/* Logo Loading Placeholder */}
              {logoLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="flex flex-col items-center space-y-3">
                    <svg className="w-16 h-16 text-white/40 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <div className="text-sm text-white/60">{t('ui.loading', 'Loading...')}</div>
                  </div>
                </div>
              )}

              {/* Logo Error Placeholder */}
              {logoError && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="flex flex-col items-center space-y-2 text-white/60">
                    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div className="text-sm">Logo unavailable</div>
                  </div>
                </div>
              )}

              <Logo
                alt="Zoe Church Logo"
                className={`w-full h-full mx-auto scale-125 lg:scale-150 md:scale-125 transition-opacity duration-300 ${logoLoading ? 'opacity-0' : 'opacity-100'}`}
                loading="eager"
                decoding="sync"
                onLoad={handleLogoLoad}
                onError={handleLogoError}
                style={{
                  filter:
                    'brightness(0) saturate(100%) invert(1) contrast(100%) drop-shadow(0 25px 50px rgba(0, 0, 0, 0.3))',
                  willChange: 'auto'
                }}
              />
            </div>
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight text-shadow-lg">
            {t('pages.home.title')}
          </h1>
          <h2 className="text-2xl md:text-3xl lg:text-4xl text-gray-100 mb-10 font-light text-shadow-md">
            {t('pages.home.subtitle')}
          </h2>
        </div>

        {/* Hero Action Buttons - Two main CTAs */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center mb-20">
          <Link to={ROUTES.EVENTS}>
            <Button
              size="md"
              className="bg-white text-black hover:bg-gray-100 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-110 backdrop-blur-sm min-w-[220px]"
            >
              {t('pages.home.hero.joinGathering')}
            </Button>
          </Link>
          <Link to={ROUTES.MINISTRIES}>
            <Button
              size="md"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-110 backdrop-blur-sm min-w-[220px]"
            >
              {t('pages.home.hero.discoverMinistries')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Enhanced Cinematic Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-40 h-40 border border-white/10 rotate-45 animate-spin-slow backdrop-blur-sm"></div>
      <div className="absolute bottom-1/4 right-10 w-32 h-32 border border-white/15 rotate-12 animate-pulse backdrop-blur-sm"></div>
      <div className="absolute top-1/2 right-20 w-6 h-6 bg-white/40 rounded-full animate-ping shadow-lg"></div>
      <div className="absolute top-1/3 left-20 w-4 h-4 bg-white/50 rounded-full animate-pulse shadow-lg"></div>
      <div className="absolute bottom-1/3 left-1/4 w-2 h-2 bg-white/60 rounded-full animate-ping"></div>
      <div className="absolute top-3/4 right-1/3 w-3 h-3 bg-white/40 rounded-full animate-pulse"></div>
    </section>
  );
};

export default HeroSection;
